import Typography from '@mui/material/Typography'
import { Box, Card, CardMedia } from '@mui/material'
import AssignmentIcon from '@mui/icons-material/Assignment'

import { concat, flatten, mergeDeepWith } from 'ramda'
import { useAppSelector } from 'src/store/hooks'
import {
  selectManySectionsById,
  selectAssignments,
  selectManyCoursesByIdGroupedBySection,
} from 'src/v3/self-paced/self-paced.slice'
import { ReactElement } from 'react'
import { useNavigate } from 'react-router-dom'
import { onCourseImageError } from 'src/v3/self-paced/utils/course-thumbnail'
import { EmptyState } from './components/empty-state'

export default (): ReactElement => {
  let navigate = useNavigate()
  let assignments = useAppSelector(selectAssignments)
  let courses = useAppSelector(
    selectManyCoursesByIdGroupedBySection(assignments),
  )
  let sections = useAppSelector(
    selectManySectionsById(
      flatten(Object.values({ ...courses }))
        .filter((x) => x !== undefined)
        .map((x) => x.sectionID),
    ),
  )

  let hasAssignments = assignments && assignments.length > 0

  return (
    <Box sx={{ bgcolor: '#000000', minHeight: '100vh', color: 'white' }}>
      <Box sx={{ px: 3, py: 4 }}>
        <Typography variant="h1" component="h1">
          Assignments
        </Typography>
      </Box>

      {!hasAssignments ? (
        <EmptyState
          icon={<AssignmentIcon />}
          title="No Assignments Yet"
          description="Users who join partner EMS services may have courses assigned to them by a manager. Once you have assignments, they will appear here for easy access."
          actionText="Browse All Courses"
          onAction={() => navigate('/self-paced')}
        />
      ) : (
        sections?.map((section) => (
          <Box className="mt-4" key={section.id}>
            <Box sx={{ px: 3, py: 2 }}>
              <Typography variant="h5" component="h5">
                {section.name}
              </Typography>
            </Box>
            <Box className="px-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {(
                  mergeDeepWith(concat, [], courses)?.[
                    (section as any).id
                  ] as any
                )?.map((item: any) => (
                  <Box
                    key={item.id}
                    sx={{
                      cursor: 'pointer',
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                      },
                      position: 'relative',
                    }}
                    onClick={() => navigate(`/self-paced/course/${item.id}`)}
                  >
                    <Card
                      sx={{
                        bgcolor: 'transparent',
                        boxShadow: 'none',
                        border: 'none',
                        borderRadius: 2,
                        overflow: 'hidden',
                      }}
                    >
                      <CardMedia
                        component="img"
                        image={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/${item.imageName}`}
                        onError={(event) =>
                          onCourseImageError(event, item.imageName)
                        }
                        alt={item.displayName}
                        sx={{
                          width: '100%',
                          height: 'auto',
                          aspectRatio: '16/9',
                          objectFit: 'cover',
                        }}
                      />
                    </Card>
                  </Box>
                ))}
              </div>
            </Box>
          </Box>
        ))
      )}
    </Box>
  )
}
